import React, { useCallback, useEffect, useState } from "react";
import { UserX, RotateCcw, CheckCircle2, AlertTriangle, Copy, Sparkles, UserPlus } from "lucide-react";
import { PageHeader, Button, Select, Card, CardContent, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError, type NoShowCampaign, type NoShowConfig, type NoShowMetrics, type OrgMember } from "@/lib/api";

const FILTERS = [
  { value: "today", label: "Today" }, { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" }, { value: "this_month", label: "This month" },
];
const STEP = (s: string) => s === "sent" ? { l: "Sent", t: "green" } : s === "unavailable" ? { l: "Unavailable — Config Required", t: "amber" } : s === "skipped" ? { l: "Skipped", t: "slate" } : { l: "Scheduled", t: "blue" };
const CAMP: Record<string, "blue" | "green" | "slate"> = { active: "blue", recovered: "green", stopped: "slate" };

export default function NoShowRecovery() {
  const [config, setConfig] = useState<NoShowConfig | null>(null);
  const [metrics, setMetrics] = useState<NoShowMetrics | null>(null);
  const [rows, setRows] = useState<NoShowCampaign[] | null>(null);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [filter, setFilter] = useState("last_30_days");
  const [processing, setProcessing] = useState(false);

  const load = useCallback(() => {
    api.noshowCampaigns().then(setRows).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { api.noshowConfig().then(setConfig).catch(() => {}); api.noshowMembers().then(setMembers).catch(() => {}); }, []);
  useEffect(() => { api.noshowMetrics(filter).then(setMetrics).catch(() => {}); }, [filter]);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setProcessing(true);
    try { const r = await api.noshowProcess(); toast.success(`Processed ${r.campaigns_scanned} workflow(s)`); load(); api.noshowMetrics(filter).then(setMetrics).catch(() => {}); }
    catch (e) { toast.error(formatApiError(e)); } finally { setProcessing(false); }
  };
  const assign = async (id: string, userId: string) => {
    try { await api.noshowAssign(id, userId && userId !== "__unassigned__" ? userId : null); toast.success("Assignment updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const stop = async (id: string) => {
    try { await api.noshowStop(id); toast.success("Workflow stopped"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cards = metrics ? [
    { label: "No-shows", value: metrics.no_shows, icon: UserX },
    { label: "Recovery attempts", value: metrics.recovery_attempts, icon: RotateCcw },
    { label: "Rescheduled", value: metrics.rescheduled, icon: CheckCircle2 },
    { label: "Recovery rate", value: `${metrics.recovery_rate}%`, icon: Sparkles },
  ] : [];
  const memberOpts = [{ value: "__unassigned__", label: "Unassigned" }, ...members.map((m) => ({ value: m.id, label: m.name }))];

  return (
    <div className="space-y-6" data-testid="page-noshow">
      <PageHeader title="No-Show Recovery" description="Win back customers who missed their appointment with an automated reschedule invite." testid="noshow-header"
        action={<Button onClick={runNow} disabled={processing} data-testid="noshow-process-btn"><RotateCcw className="h-4 w-4" /> {processing ? "Processing…" : "Run recovery now"}</Button>} />

      {config && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="noshow-config">
          <StatusBadge tone="blue">{config.plan} plan</StatusBadge>
          {config.included ? <StatusBadge tone="green">No-show recovery included ({config.max_steps}-step)</StatusBadge>
            : <StatusBadge tone="amber">Not included — upgrade to Growth+</StatusBadge>}
          {config.ai_entitled && <StatusBadge tone={config.ai_available ? "green" : "slate"}>AI win-back {config.ai_available ? "available" : ""}</StatusBadge>}
          {config.included && (config.sms_configured ? <StatusBadge tone="green">SMS live</StatusBadge>
            : <StatusBadge tone="amber"><AlertTriangle className="mr-1 inline h-3 w-3" /> SMS: configuration required</StatusBadge>)}
        </div>
      )}

      {config && !config.included && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700" data-testid="noshow-upgrade">
          No-show recovery is available on Growth and above. Missed appointments are still tracked here — upgrade to automatically invite customers to reschedule.
        </div>
      )}
      {config?.included && !config?.sms_configured && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="noshow-honest-note">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-xs text-amber-800">Win-back messages are scheduled automatically, but SMS sending is paused until Twilio is live. Due steps are marked <b>“Unavailable — Configuration Required”</b> and are <b>never</b> recorded as sent. Two-way AI conversation also requires live telephony.</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-800">Performance</h2>
        <div className="w-44"><Select value={filter} onChange={setFilter} options={FILTERS} testid="noshow-metrics-filter" /></div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="noshow-metrics">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500"><c.icon className="h-4 w-4 text-blue-500" /> {c.label}</div>
            <div className="mt-2 font-display text-2xl font-semibold text-slate-900">{c.value}</div>
          </div>
        ))}
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Recovery workflows</h2>
        {rows === null ? <LoadingBlock rows={4} /> : rows.length === 0 ? (
          <EmptyState icon={UserX} title="No missed appointments yet" description="When an appointment is marked no-show, a recovery workflow starts here." />
        ) : (
          <div className="space-y-3" data-testid="noshow-campaigns">
            {rows.map((c) => (
              <Card key={c.id} data-testid={`noshow-campaign-${c.id}`}>
                <CardContent className="p-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-slate-900">{c.contact_name} <span className="text-xs font-normal text-slate-400">· {c.service ?? "appointment"}</span></p>
                      <div className="mt-1 flex items-center gap-2">
                        <StatusBadge tone={CAMP[c.status] ?? "slate"}>{c.status}</StatusBadge>
                        <span className="text-xs text-slate-500">{c.plan} plan</span>
                        {c.stop_reason && <StatusBadge tone="slate">{c.stop_reason}</StatusBadge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 text-xs text-slate-500"><UserPlus className="h-3.5 w-3.5" />
                        <div className="w-40"><Select value={c.assigned_to ?? "__unassigned__"} onChange={(v) => assign(c.id, v)} options={memberOpts} testid={`noshow-assign-${c.id}`} /></div>
                      </div>
                      {c.status === "active" && <Button size="sm" variant="outline" onClick={() => stop(c.id)} data-testid={`noshow-stop-${c.id}`}>Stop</Button>}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {c.steps.map((s, i) => {
                      const m = STEP(s.state);
                      return (
                        <div key={i} className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs" data-testid={`noshow-step-${c.id}-${i}`}>
                          <span className="text-slate-600">SMS</span><StatusBadge tone={m.t as any}>{m.l}</StatusBadge>
                          {s.ai_generated && <StatusBadge tone="blue">AI</StatusBadge>}
                        </div>
                      );
                    })}
                    {c.customer_link && (
                      <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(c.customer_link!); toast.success("Reschedule link copied"); }} data-testid={`noshow-link-${c.id}`}>
                        <Copy className="h-3.5 w-3.5" /> Reschedule link
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
